import { forwardRef } from 'react'
import { Search, X } from 'lucide-react'
import { Input, type InputProps } from './Input.js'
import { Button } from './Button.js'

export interface SearchInputProps extends Omit<InputProps, 'leading' | 'trailing' | 'type'> {
  /** Shown when there is something typed, and clears it. */
  onClear?: () => void
}

/**
 * The box at the top of a table.
 *
 * A clear button rather than only a glass: on a desk somebody filters, reads, and
 * wants the whole list back, and selecting the text to delete it is three actions
 * where one will do.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { onClear, value, ...props },
  ref,
) {
  const hasValue = typeof value === 'string' && value.length > 0
  return (
    <Input
      ref={ref}
      /**
       * `text`, not `search`.
       *
       * `type="search"` makes WebKit draw **its own** clear cross inside the
       * field — so every search box on the desk had two crosses side by side,
       * one the browser's and one the component's, and only one of them did
       * what this screen means by clearing. `role` keeps the semantics a screen
       * reader needs without the second button.
       */
      type="text"
      role="searchbox"
      value={value}
      leading={<Search />}
      trailing={
        hasValue && onClear ? (
          /* A 36px square inside a 40px box left 2px of air above and below
             and read as a second control bolted onto the field. Round, 28px,
             and the cross inside it is still `size-4` — the button shrinks to
             the icon rather than the icon shrinking with the button. */
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Clear the search"
            onClick={onClear}
            className="-mr-1.5 size-7 rounded-full"
          >
            <X />
          </Button>
        ) : undefined
      }
      {...props}
    />
  )
})
